import React, { useCallback, useEffect, useState } from 'react';

interface LetterSpacingInputProps {
  /** Letter spacing as an integer percent (0 = default). */
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  /** Optional title for accessibility */
  title?: string;
}

/**
 * Letter spacing input (percent) with editable number field plus stacked
 * +/- buttons. Mirrors FontSizeInput but accepts negative integers.
 * - Up/Down arrow keys while focused increment/decrement by 1
 * - Stacked +/- buttons clamp to min/max
 * - Value commits on blur or Enter
 */
const LetterSpacingInput: React.FC<LetterSpacingInputProps> = ({
  value,
  onChange,
  min = -100,
  max = 100,
  disabled = false,
  title = 'Letter spacing (%)',
}) => {
  const [draft, setDraft] = useState<string>(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const clamp = useCallback((n: number) => Math.min(max, Math.max(min, n)), [min, max]);

  const commit = useCallback(() => {
    const parsed = parseInt(draft, 10);
    if (Number.isFinite(parsed)) {
      const next = clamp(parsed);
      if (next !== value) onChange(next);
      setDraft(String(next));
    } else {
      setDraft(String(value));
    }
  }, [draft, value, onChange, clamp]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      onChange(clamp(value + 1));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      onChange(clamp(value - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div
      className="flex items-stretch"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="relative flex items-stretch">
        <input
          type="text"
          inputMode="numeric"
          value={draft}
          // Allow an optional leading minus followed by digits only.
          onChange={(e) => setDraft(e.target.value.replace(/(?!^-)[^0-9]/g, ''))}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
          disabled={disabled}
          title={title}
          aria-label={title}
          className="w-10 h-6 text-[11px] leading-none text-center bg-black/40 text-white rounded-l border border-r-0 border-white/20 focus:outline-none focus:border-white/60"
          style={{ paddingInline: 0, paddingRight: 10 }}
        />
        <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-[9px] text-white/50">%</span>
      </div>
      <div className="flex flex-col">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(clamp(value + 1)); }}
          disabled={disabled}
          aria-label="Increase letter spacing"
          className="w-3 h-3 leading-none text-[10px] text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-tr border border-white/20 border-b-0 flex items-center justify-center"
        >
          +
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(clamp(value - 1)); }}
          disabled={disabled}
          aria-label="Decrease letter spacing"
          className="w-3 h-3 leading-none text-[10px] text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-br border border-white/20 flex items-center justify-center"
        >
          −
        </button>
      </div>
    </div>
  );
};

export default LetterSpacingInput;
